/**
 * useSavedUsers Hook
 *
 * Feature-specific hook for the Saved Users ("Favourite people") page.
 *
 * Architecture: Page → Hook → UseCase → Service
 *
 * Mirrors Flutter's SavedUsersScreenState
 * (lib/presentation/screens/user/saved_users_screen.dart).
 *
 * Responsibilities:
 * - Snapshot the locally-stored SavedUser records once on init via
 *   `savedUsersService.listLocalSavedUsers()` and paginate against that
 *   snapshot, matching Flutter's `_getData`
 *   (saved_users_screen.dart:119-171).
 * - Listen reactively to secure-storage writes under the SAVED_USERS
 *   prefix (`SavedUser_`) via `useListenToSecureStorageUpdatesByPrefix`
 *   with `loadInitialItems: false`. The hook is the canonical
 *   live-update channel; `loadInitialItems` is `false` because `init()`
 *   owns the initial load through the domain service. This matches the
 *   pattern used by `useLikedVideos` (see lines 60-71 / 169-255 there)
 *   and `useUnlockedVideos`.
 * - SavedUserStorage has no `liked` boolean, so an unsave is a
 *   `remove(...)` (see `saveUserToFavoritesUseCase.ts`). The hook
 *   surfaces those as entries in `deletedKeys`; the live-update effect
 *   below partitions live state into removes (via `deletedKeys`) and
 *   adds (via `liveLocalItems`).
 * - Surface loading / loading-more / hasMore / error to the UI.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useListenToSecureStorageUpdatesByPrefix } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { SavedUserStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { RootState } from '@/presentation/store';
import { savedUsersService } from '@/domain/services/savedUser/savedUsersService';
import { fetchSavedUsersPageUseCase } from '@/domain/usecases/savedUser/fetchSavedUsersPageUseCase';
import { userService } from '@/domain/services/user/userService';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';

export interface UseSavedUsersResult {
  users: UserEntity[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  init: () => Promise<void>;
  loadMore: () => Promise<void>;
}

export const useSavedUsers = (): UseSavedUsersResult => {
  const { l10n } = useLocale();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Pure live-update channel — `loadInitialItems: false` because `init()`
  // below loads the existing items explicitly through the service. The
  // hook's `items` starts empty and only grows when a new save is written
  // while the page is open. Deletes (SavedUserStorage has no `liked` flag,
  // so unsaves are `remove(...)` writes) arrive on `deletedKeys`.
  const { items: liveLocalItems, deletedKeys: liveDeletedKeys } =
    useListenToSecureStorageUpdatesByPrefix<SavedUserStorage>(
      SECURE_STORAGE_KEYS.SAVED_USERS,
      (item) => item.firebaseId,
      false,
    );

  const [users, setUsers] = useState<UserEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The snapshot is taken once on init and is the basis for every page
  // fetch — matches Flutter's `_savedUsers`, which is read once
  // (synchronously, in `initState`) and re-sliced by `_getData`.
  const localItemsSnapshotRef = useRef<SavedUserStorage[]>([]);
  const pageIndexRef = useRef(0);
  // Every firebaseId already covered by the pagination snapshot or by a
  // subsequent live-add. Used to:
  //  - prevent a duplicate prepend when a `setEncrypted` event lands for
  //    an id that was already in the snapshot (e.g. a race between
  //    `listLocalSavedUsers` and a save toggle).
  //  - allow a re-save after an unsave to be treated as a new add (the
  //    delete branch below removes the id from this set).
  //  - dedupe the append-only `deletedKeys` stream surfaced by the live
  //    hook (an id only triggers a remove on the first observation).
  const seenIdsRef = useRef<Set<string>>(new Set());
  // Guard so the live-update effect doesn't act before init has resolved
  // (otherwise initial ids would be treated as "new" and double-fetched).
  const hasInitializedRef = useRef(false);
  const isLoadingMoreRef = useRef(false);

  const init = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const snapshot = await savedUsersService.listLocalSavedUsers();
      localItemsSnapshotRef.current = snapshot;
      pageIndexRef.current = 0;

      const result = await fetchSavedUsersPageUseCase.execute({
        localItems: snapshot,
        pageIndex: 0,
        currentUser,
      });

      // Seed with EVERY id in the snapshot — see the ref's declaration
      // for the rationale.
      const initialSeen = new Set<string>();
      for (const item of snapshot) {
        initialSeen.add(item.firebaseId);
      }
      seenIdsRef.current = initialSeen;

      setUsers(result.users);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error fetching saved users', 'useSavedUsers.init', { error: err as Error });
      setError(l10n.savedUsers.errorLoading);
      setUsers([]);
      setHasMore(false);
    } finally {
      // Set the ref before flipping isLoading: the live-update effect
      // gates on !isLoading and reads this ref, so the ref must be true
      // by the time the re-render from setIsLoading(false) fires it.
      hasInitializedRef.current = true;
      setIsLoading(false);
    }
  }, [currentUser, l10n]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (isLoadingMoreRef.current || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    setError(null);

    try {
      const nextIndex = pageIndexRef.current + 1;
      const result = await fetchSavedUsersPageUseCase.execute({
        localItems: localItemsSnapshotRef.current,
        pageIndex: nextIndex,
        currentUser,
      });

      pageIndexRef.current = nextIndex;
      setUsers((prev) => [...prev, ...result.users]);
      setHasMore(result.hasMore);
    } catch (err) {
      appLogger.error('Error loading more saved users', 'useSavedUsers.loadMore', { error: err as Error });
      setError(l10n.savedUsers.loadMoreFailed);
    } finally {
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    }
  }, [hasMore, currentUser, l10n]);

  // Reactive add/remove for save toggles — mirrors Flutter
  // `_onBoxDataEvent` (saved_users_screen.dart:87-117). Routed through
  // `useListenToSecureStorageUpdatesByPrefix` (see top-of-file rationale):
  //
  //  - `liveDeletedKeys` is the append-only stream of `remove(...)`
  //    events under the SAVED_USERS prefix (SavedUserStorage has no
  //    `liked` flag, so this is the only delete signal). We dedupe via
  //    `seenIdsRef`: an id only triggers a remove on the first
  //    observation, and is then dropped from the set so a subsequent
  //    re-save is treated as a new add.
  //  - `liveLocalItems` contains every saved-user write the hook has
  //    seen since mount. We dedupe via `seenIdsRef` to avoid double-
  //    fetching ids already in the initial snapshot.
  //
  // Structural divergence vs `useLikedVideos`: that hook can partition
  // a single `liveLocalItems` array into removes + adds because
  // `UserMediaLikeStorage.liked: false` keeps the unliked record in
  // `items`. Our delete path is a `remove(...)` (no `liked` flag), so
  // the hook drops the entry from `items` and surfaces it via
  // `deletedKeys` instead — see the JSDoc on
  // `useListenToSecureStorageUpdatesByPrefix`.
  //
  // `isLoading` is a dep (not just the ref) so the effect re-runs after
  // init finishes — covers the race where a live write lands between
  // `listLocalSavedUsers` resolving and `setIsLoading(false)`.
  useEffect(() => {
    if (!hasInitializedRef.current || isLoading) {
      return;
    }

    // Apply removes synchronously — no Firestore lookup needed.
    if (liveDeletedKeys.length > 0) {
      const removedIds = new Set<string>();
      for (const id of liveDeletedKeys) {
        if (seenIdsRef.current.has(id)) {
          removedIds.add(id);
          seenIdsRef.current.delete(id);
        }
      }
      if (removedIds.size > 0) {
        setUsers((prev) => prev.filter((user) => !removedIds.has(user.firebaseId)));
      }
    }

    // Partition adds: anything in `liveLocalItems` whose id is not yet
    // in `seenIdsRef` is a new save we need to resolve.
    const adds: SavedUserStorage[] = [];
    for (const item of liveLocalItems) {
      if (!seenIdsRef.current.has(item.firebaseId)) {
        adds.push(item);
      }
    }

    if (adds.length === 0) {
      return;
    }

    let cancelled = false;

    (async () => {
      for (const item of adds) {
        try {
          const user = await userService.fetchUserDetails(item.firebaseId);
          if (cancelled) {
            return;
          }
          // Mark as seen even when `fetchUserDetails` returned null
          // (user was filtered/not-found) — avoids re-fetching on every
          // subsequent storage event for this id. Mirrors
          // `useLikedVideos.ts:225-238`.
          seenIdsRef.current.add(item.firebaseId);
          if (user) {
            setUsers((prev) => [user, ...prev]);
          }
        } catch (err) {
          appLogger.error(
            'Error resolving newly-saved user',
            'useSavedUsers.onLiveAdd',
            { error: err as Error, context: { firebaseId: item.firebaseId } },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [liveLocalItems, liveDeletedKeys, isLoading]);

  return {
    users,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    init,
    loadMore,
  };
};
